/**
 * `<NewEncounterForm>` — the **create-new-encounter** view (ENCOUNTER.app's ＋ NEW window). It
 * collects an encounter's **name** and **grid mode** and then mints the encounter via the
 * world-level `POST /api/world/encounters` (GM host-key authed). On success the desktop opens the
 * new encounter in {@link CombatApp}.
 *
 * **Why this is a plain form, not a ws-room client:** name + grid mode are chosen *before the
 * encounter exists*, so there's nothing to join yet. Creating the **container** is a REST mint
 * (the lazily-created encounter room is seeded with these values); mutating its **contents** later
 * is the command path. So this is local state → one REST call, deliberately — it is NOT an editor
 * of a live encounter.
 *
 * **v1 grid modes:** the picker only enables `2d-square`; the others are listed but disabled
 * ("soon"). The reducer accepts any known mode, but the v1 restriction lives here (and at the
 * create endpoint).
 */
import { useState } from "react";
import { DEFAULT_GRID_MODE, GRID_MODES, type GridMode } from "@lancer-vtt/shared";
import { createEncounter } from "../../lib/api";
import "./brutalist-editor.css";

export function NewEncounterForm({
  hostKey,
  onCreated,
  onCancel,
}: {
  /** The GM host key — required to mint; null disables the CREATE action. */
  hostKey: string | null;
  /** Called with the new encounter's id after a successful mint (desktop opens COMBAT.app). */
  onCreated: (id: string) => void;
  /** Close the form without creating. */
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [gridMode, setGridMode] = useState<GridMode>(DEFAULT_GRID_MODE);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  const canCreate = !!hostKey && trimmed !== "" && !busy;

  const create = async () => {
    if (!hostKey || trimmed === "" || busy) return;
    setBusy(true);
    setError(null);
    const created = await createEncounter(trimmed, gridMode, hostKey);
    if (created.ok) onCreated(created.value.id);
    else setError(created.error);
    setBusy(false);
  };

  return (
    <div className="be-doc">
      {error && <div className="be-error">REJECTED · {error}</div>}

      <div className="be-row">
        <div className="be-celllabel">NAME</div>
        <div className="be-cellbody">
          <input
            className="be-input be-input-callsign"
            placeholder="UNNAMED ENCOUNTER"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canCreate) void create();
              if (e.key === "Escape") onCancel();
            }}
          />
        </div>
      </div>

      <div className="be-row">
        <div className="be-celllabel">GRID MODE</div>
        <div className="be-cellbody">
          <div className="be-caption">2D · SQUARE ONLY FOR NOW</div>
          <div className="be-enc-modes">
            {GRID_MODES.map((g) => (
              <button
                key={g.value}
                type="button"
                className={`be-enc-mode${gridMode === g.value ? " active" : ""}`}
                disabled={!g.supported}
                title={g.supported ? g.label : `${g.label} — coming soon`}
                onClick={() => setGridMode(g.value)}
              >
                {g.label}
                {g.supported ? "" : " · soon"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="be-footer">
        <button type="button" className="be-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="be-btn be-btn-primary" disabled={!canCreate} onClick={() => void create()}>
          {busy ? "Creating…" : "Create Encounter"}
        </button>
      </div>
    </div>
  );
}
